import { Entity, generateEntityId } from './entity';
import type { Vector2 } from '../core/constants';
import { getGameRNG } from '../core/rng';

export interface FireConfig {
  x: number;
  y: number;
  duration?: number;
  damagePerSecond?: number;
  spreadChance?: number;
  burnRadius?: number;
  burnInterval?: number;
}

export class Fire implements Entity {
  id: string;
  position: Vector2;
  velocity: Vector2 = { x: 0, y: 0 };
  active = true;

  duration: number;
  damagePerSecond: number;
  spreadChance: number;
  burnRadius: number;
  burnInterval: number;

  private lifetime: number = 0;
  private damageAccumulator: number = 0;
  private hasSpread: boolean = false;
  private flickerPhase: number = 0;
  private burnTimer: number = 0;

  onDamageWorm?: (wormId: string, damage: number) => void;
  onSpread?: (x: number, y: number) => void;
  onBurnTerrain?: (x: number, y: number, radius: number) => void;
  onExtinguish?: () => void;
  onFall?: () => void;

  constructor(config: FireConfig) {
    this.id = generateEntityId('fire');
    this.position = { x: config.x, y: config.y };
    this.duration = config.duration ?? 5;
    this.damagePerSecond = config.damagePerSecond ?? 5;
    this.spreadChance = config.spreadChance ?? 0.1;
    this.burnRadius = config.burnRadius ?? 4;
    this.burnInterval = config.burnInterval ?? 0.3;
    this.flickerPhase = getGameRNG().nextFloat(0, Math.PI * 2);
  }

  update(deltaTime: number): void {
    if (!this.active) return;

    this.lifetime += deltaTime;
    this.flickerPhase += deltaTime * 10;

    if (this.lifetime >= this.duration) {
      this.extinguish();
      return;
    }

    this.velocity.y += 400 * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    this.onFall?.();

    this.burnTimer += deltaTime;
    if (this.burnTimer >= this.burnInterval) {
      this.burnTimer = 0;
      this.onBurnTerrain?.(this.position.x, this.position.y, this.burnRadius);
    }

    if (
      !this.hasSpread &&
      this.lifetime > 0.5 &&
      getGameRNG().next() < this.spreadChance * deltaTime
    ) {
      this.hasSpread = true;
      const spreadX = this.position.x + getGameRNG().nextFloat(-15, 15);
      const spreadY = this.position.y + getGameRNG().nextFloat(-7.5, 7.5);
      this.onSpread?.(spreadX, spreadY);
    }
  }

  checkWormCollision(wormX: number, wormY: number, wormWidth: number, wormHeight: number): boolean {
    const fireRadius = 8;
    const wormCenterX = wormX + wormWidth / 2;
    const wormCenterY = wormY + wormHeight / 2;

    const dx = this.position.x - wormCenterX;
    const dy = this.position.y - wormCenterY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    return distance < fireRadius + Math.max(wormWidth, wormHeight) / 2;
  }

  accumulateDamage(deltaTime: number): number {
    this.damageAccumulator += this.damagePerSecond * deltaTime;

    if (this.damageAccumulator >= 1) {
      const damage = Math.floor(this.damageAccumulator);
      this.damageAccumulator -= damage;
      return damage;
    }

    return 0;
  }

  getFlickerIntensity(): number {
    return 0.7 + 0.3 * Math.sin(this.flickerPhase);
  }

  getRemainingLife(): number {
    return Math.max(0, 1 - this.lifetime / this.duration);
  }

  extinguish(): void {
    if (!this.active) return;
    this.active = false;
    this.onExtinguish?.();
  }

  destroy(): void {
    this.active = false;
  }
}
